import React from "react";
import { Select, Radio } from "antd";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import moment from "moment";

// graphql
import GET_CATEGORIES from "graphql/queries/GetCategories";
import GET_LANGUAGES from "graphql/queries/GetLanguages";
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
// import types for antd components
import { RadioChangeEvent } from "antd/lib/radio";
import { SelectValue } from "antd/lib/select";
// custom types
import {
  Category,
  City,
  CategoryState,
  CityState,
  DayState,
  Level,
  Language,
} from "types";
import { filterActiveLevelsAndCurrentUserLevel } from "utils/language";

// styling
import {
  FilterContainer,
  FilterMenuRow,
  FilterMenuCol,
  FilterLabel,
  AdvancedFilterMenuRow,
  LevelSelect,
  ClassTypeFilter,
  DayFilter,
  DayOption,
  TimeFilter,
  CategoriesFilter,
  CategoryOption,
  StudiedTopicsSelect,
  TopicSelect,
  CharacterSelect,
  CityFilter,
  CityOption,
} from "./ClassFilterStyle";

const { Option } = Select;

type ClassFilterProps = {
  onChangeDaysCategoriesCities: (
    field: "days" | "categories" | "cities",
    name: string
  ) => void;
  onChangeOtherFilterFields: (
    field: string,
    value: string | number | number[] | boolean
  ) => void;
  filter: {
    level: {
      id: string;
    };
    classType: string;
    days: DayState[];
    time: number[];
    includeStudiedTopics: boolean;
    excludeAllCityVerion: boolean;
    categories: CategoryState[];
    spokenOnly: boolean;
    numberOfTopicsAhead: number;
    charactersPreference: string;
    cities: CityState[];
    isChineseLanguage: boolean;
    language: {
      id: string;
    };
  };
};

// timeStamps for the slider. See antd documentation for slider for more details
const timeStamps = {
  0: {
    style: {
      transform: "translateX(-20%)",
    },
    label: `0:00`,
  },
  12: "12:00",
  23: {
    style: {
      transform: "translateX(-95%)",
    },
    label: `23:00`,
  },
};

function ClassFilter(props: ClassFilterProps) {
  const { t } = useTranslation("book_a_class");

  const { onChangeDaysCategoriesCities, onChangeOtherFilterFields, filter } =
    props;

  // LEVEL DATA MANAGEMENT
  const { data: categoryList, error: categoryError } = useQuery(
    GET_CATEGORIES,
    {
      variables: {
        levelId: filter.level.id,
      },
      skip: filter.level.id === "0",
    }
  );
  const { data: languageList, error: languageError } = useQuery(GET_LANGUAGES);
  const { data: currentUserInfo } = useQuery(GET_CURRENT_USER_INFO, {
    fetchPolicy: "network-only",
  });

  if (categoryError) {
    console.log(categoryError);
  }

  if (languageError) {
    console.log(languageError);
  }
  // HELPER FUNCTIONS
  const isOptionChosen = (
    name: string,
    arr: DayState[] | CategoryState[] | CityState[]
  ) => {
    const findResult = arr.find(
      (option: DayState | CategoryState | CityState) => {
        return option.name === name;
      }
    );
    return findResult?.chosen as boolean;
  };

  const enMoment = moment();
  enMoment.locale("en");

  const days = moment.weekdaysShort().map((wd, index) => ({
    label: wd,
    value: enMoment.localeData().weekdaysShort()[index],
  }));

  const studyLanguage = languageList?.languages.find((language: Language) => {
    return language.id === filter.language.id;
  });

  const levelOptions = filterActiveLevelsAndCurrentUserLevel(
    currentUserInfo?.me?.currentLevel?.id,
    studyLanguage?.levels
  );
  // RENDERER
  return (
    <FilterContainer>
      <FilterMenuRow gutter={16}>
        <FilterMenuCol xs={24} sm={12} md={6}>
          <LevelSelect
            className="filter-level"
            showSearch
            placeholder={t("My Level")}
            optionFilterProp="children"
            value={filter.level.id}
            onChange={(value: SelectValue) => {
              onChangeOtherFilterFields("level", value as string);
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {levelOptions.map((level: Level) => (
              <Option
                value={level.id}
                key={level.title}
                disabled={!level.active}
              >
                {level.title}
              </Option>
            ))}
          </LevelSelect>
        </FilterMenuCol>
        <FilterMenuCol xs={24} sm={12} md={5} lg={4}>
          <ClassTypeFilter
            defaultValue="small_group"
            value={filter.classType}
            onChange={(ev: RadioChangeEvent) => {
              onChangeOtherFilterFields("classType", ev.target.value);
            }}
          >
            <Radio.Button value="small_group">{t("Group")}</Radio.Button>
            <Radio.Button value="one_on_one">{t("1-on-1")}</Radio.Button>
          </ClassTypeFilter>
        </FilterMenuCol>
        <FilterMenuCol xs={24} sm={24} md={8} lg={9}>
          <DayFilter>
            {/* eslint-disable-next-line react/no-unused-prop-types */}
            {days.map(({ label, value }: { label: string; value: string }) => {
              return (
                <DayOption
                  key={value}
                  data-chosen={isOptionChosen(value, filter.days)}
                  onClick={() => {
                    onChangeDaysCategoriesCities("days", value);
                  }}
                >
                  {label}
                </DayOption>
              );
            })}
          </DayFilter>
        </FilterMenuCol>
        <FilterMenuCol xs={24} sm={24} md={5}>
          <TimeFilter
            step={1}
            min={0}
            max={23}
            marks={timeStamps}
            range
            defaultValue={[0, 23]}
            tipFormatter={(value) => `${value}:00`}
            onChange={(value: [number, number]) => {
              onChangeOtherFilterFields("time", value);
            }}
          />
        </FilterMenuCol>
      </FilterMenuRow>

      <AdvancedFilterMenuRow gutter={16}>
        <FilterMenuCol xs={24} sm={24} md={24} lg={20} xl={17}>
          <CategoriesFilter gutter={4}>
            <FilterLabel>{t("CATEGORIES")}:</FilterLabel>
            {categoryList?.categories.map((category: Category) => (
              <CategoryOption
                key={category.id}
                data-chosen={isOptionChosen(category.name, filter.categories)}
                onClick={() => {
                  // only update the state if category is NOT marked as skippedIfSpokenOnly
                  // or if the spokenOnly option has NOT been checked
                  if (!category.skippedIfSpokenOnly || !filter.spokenOnly) {
                    onChangeDaysCategoriesCities("categories", category.name);
                  }
                }}
              >
                {category.name}
              </CategoryOption>
            ))}
          </CategoriesFilter>
        </FilterMenuCol>
        <FilterMenuCol xs={24} sm={24} md={12} lg={10} xl={7}>
          <CityFilter>
            <FilterLabel>{t("CITY VERSIONS")}:</FilterLabel>
            {studyLanguage?.cities.map((city: City) => (
              <CityOption
                key={city.id}
                data-chosen={
                  filter.charactersPreference === "simplified"
                    ? isOptionChosen(city.name, filter.cities)
                    : isOptionChosen(city.name, filter.cities) &&
                      city.name === "TP"
                }
                onClick={() => {
                  if (filter.charactersPreference === "simplified") {
                    onChangeDaysCategoriesCities("cities", city.name);
                  } else if (city.name === "TP") {
                    onChangeDaysCategoriesCities("cities", city.name);
                  }
                }}
              >
                {city.name}
              </CityOption>
            ))}
          </CityFilter>
        </FilterMenuCol>
      </AdvancedFilterMenuRow>

      <AdvancedFilterMenuRow gutter={16}>
        <FilterMenuCol xs={24} sm={24} md={12} lg={8} xl={7}>
          <StudiedTopicsSelect
            placeholder={t("Not studied topics only")}
            optionFilterProp="children"
            defaultValue={
              Number(filter.includeStudiedTopics) +
              Number(filter.excludeAllCityVerion)
            }
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={(value: SelectValue) => {
              onChangeOtherFilterFields(
                "includeStudiedTopics",
                value as number
              );
            }}
          >
            <Option value={1}>{t("Not Yet Studied or Booked Topics")}</Option>
            <Option value={2}>{t("All Topics")}</Option>
          </StudiedTopicsSelect>
        </FilterMenuCol>
        <FilterMenuCol xs={24} sm={24} md={8} lg={4} xl={5}>
          <TopicSelect
            showSearch
            placeholder={t("All topic")}
            optionFilterProp="children"
            defaultValue={50}
            getPopupContainer={(trigger) => trigger.parentNode}
            onChange={(value: SelectValue) => {
              onChangeOtherFilterFields("numberOfTopicsAhead", value as number);
            }}
          >
            <Option value={50}>{t("All Chapters")}</Option>
            <Option value={1}>{t("One Topic Ahead (1 topic)")}</Option>
            <Option value={5}>{t("Half a Chapter ahead (5 topics)")}</Option>
            <Option value={10}>{t("One Chapter ahead (10 topics)")}</Option>
          </TopicSelect>
        </FilterMenuCol>
        {filter.isChineseLanguage ? (
          <>
            <FilterMenuCol xs={24} sm={24} md={10} lg={6} xl={5}>
              <CharacterSelect
                showSearch
                optionFilterProp="children"
                defaultValue={filter.charactersPreference}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value: SelectValue) => {
                  onChangeOtherFilterFields(
                    "charactersPreference",
                    value as string
                  );
                }}
              >
                <Option value="simplified">{t("Simplified Characters")}</Option>
                <Option value="traditional">
                  {t("Traditional Characters")}
                </Option>
              </CharacterSelect>
            </FilterMenuCol>
            <FilterMenuCol xs={24} sm={24} md={8} lg={6} xl={5}>
              <CharacterSelect
                showSearch
                optionFilterProp="children"
                defaultValue={filter.spokenOnly.toString()}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value: SelectValue) => {
                  onChangeOtherFilterFields("spokenOnly", value as string);
                }}
              >
                <Option value="false">{t("Spoken & Written")}</Option>
                <Option value="true">{t("Spoken Only")}</Option>
              </CharacterSelect>
            </FilterMenuCol>
          </>
        ) : null}
      </AdvancedFilterMenuRow>
    </FilterContainer>
  );
}

export default ClassFilter;
